import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, FileText, Download, Send, 
  CheckCircle, Clock, AlertCircle, MessageSquare,
  Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplicationDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [newNote, setNewNote] = useState('');
  const [docRequest, setDocRequest] = useState({ type: '', message: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/portal/dashboard');
      return;
    }
    fetchApplication();
  }, [userId, user]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications/${userId}`, { withCredentials: true });
      setApplication(response.data);
      setNewStatus(response.data.profile?.status || 'pending');
    } catch (error) {
      toast.error('Failed to load application');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/applications/${userId}/status`, {
        status: newStatus,
        notes: statusNote
      }, { withCredentials: true });
      toast.success('Status updated');
      setStatusNote('');
      fetchApplication();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setUpdating(true);
    try {
      await axios.post(`${API}/admin/applications/${userId}/notes`, {
        note: newNote
      }, { withCredentials: true });
      toast.success('Note added');
      setNewNote('');
      fetchApplication();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setUpdating(false);
    }
  };

  const handleDocumentRequest = async () => {
    if (!docRequest.type || !docRequest.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setUpdating(true);
    try {
      await axios.post(`${API}/admin/applications/${userId}/request-document`, {
        document_type: docRequest.type,
        message: docRequest.message
      }, { withCredentials: true });
      toast.success('Document request sent');
      setDocRequest({ type: '', message: '' });
      fetchApplication();
    } catch (error) {
      toast.error('Failed to send request');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await axios.get(`${API}/admin/documents/${docId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      in_review: Clock,
      approved: CheckCircle,
      rejected: AlertCircle,
      documents_needed: AlertCircle
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  const { profile, user: appUser, documents, notes } = application;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-navy text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-slate-300 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Applicant Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                    <User className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-navy">{profile?.full_legal_name}</h1>
                    <p className="text-slate-500">{appUser?.email}</p>
                    <p className="text-sm text-slate-400">ID: {profile?.application_id}</p>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Date of Birth:</span>
                  <p className="font-medium">{profile?.date_of_birth}</p>
                </div>
                <div>
                  <span className="text-slate-500">Phone:</span>
                  <p className="font-medium">{profile?.phone}</p>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500">Address:</span>
                  <p className="font-medium">
                    {profile?.address}, {profile?.city}, {profile?.state} {profile?.zip_code}, {profile?.country}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Occupation:</span>
                  <p className="font-medium">{profile?.occupation || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Income Range:</span>
                  <p className="font-medium">{profile?.income_range || '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500">Policy Interests:</span>
                  <p className="font-medium">{profile?.policy_interests?.join(', ') || '-'}</p>
                </div>
                {profile?.beneficiary_name && (
                  <div className="sm:col-span-2">
                    <span className="text-slate-500">Beneficiary:</span>
                    <p className="font-medium">
                      {profile?.beneficiary_name} ({profile?.beneficiary_relationship})
                    </p>
                  </div>
                )}
                {profile?.existing_insurance && (
                  <div className="sm:col-span-2">
                    <span className="text-slate-500">Existing Insurance:</span>
                    <p className="font-medium">{profile?.existing_insurance}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Documents */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents ({documents?.length || 0})
              </h2>

              {documents?.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No documents uploaded</p>
              ) : (
                <div className="space-y-2">
                  {documents?.map((doc) => (
                    <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.filename}</p>
                          <p className="text-xs text-slate-500">{doc.document_type}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.doc_id, doc.filename)}
                        data-testid={`download-${doc.doc_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Request Document */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Request Document</h3>
                <div className="space-y-3">
                  <Input
                    placeholder="Document type (e.g., Medical Records)"
                    value={docRequest.type}
                    onChange={(e) => setDocRequest({ ...docRequest, type: e.target.value })}
                    data-testid="doc-request-type"
                  />
                  <Textarea
                    placeholder="Message to customer..."
                    value={docRequest.message}
                    onChange={(e) => setDocRequest({ ...docRequest, message: e.target.value })}
                    className="min-h-[80px]"
                    data-testid="doc-request-message"
                  />
                  <Button
                    onClick={handleDocumentRequest}
                    disabled={updating}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="send-doc-request"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Request
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Update */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-navy mb-4">Update Status</h2>
              
              <div className="space-y-4">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_review">In Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="documents_needed">Documents Needed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Textarea
                  placeholder="Add a note (optional)"
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="status-note"
                />
                
                <Button
                  onClick={handleStatusUpdate}
                  disabled={updating}
                  className="w-full bg-navy hover:bg-slate-800 text-white"
                  data-testid="update-status-btn"
                >
                  Update Status
                </Button>
              </div>
            </motion.div>

            {/* Admin Notes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Notes
              </h2>

              {/* Add Note */}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  data-testid="add-note-input"
                />
                <Button
                  onClick={handleAddNote}
                  disabled={updating || !newNote.trim()}
                  size="icon"
                  className="bg-navy hover:bg-slate-800"
                  data-testid="add-note-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Notes List */}
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {notes?.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-2">No notes yet</p>
                ) : (
                  notes?.map((note, index) => (
                    <div key={index} className="p-3 bg-slate-50 rounded-lg text-sm">
                      <p className="text-slate-700">{note.note}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail;
